import { Platform } from 'react-native';
import { useAppContext, Screen } from '../contexts/AppContext';

/**
 * The single "go back" primitive — replaces the ~50+ hand-copied
 * `if (Platform.OS === 'web') window.history.back(); else navigateTo(X)`
 * blocks scattered across screen files. On web this steps through the REAL
 * browser history (so Back always returns to wherever the user actually
 * came from, not a hardcoded destination the screen author guessed at) —
 * this only works correctly because AppNavigator.tsx's navigateTo() now
 * calls pushState for every genuine navigation (fixed as part of the
 * navigation overhaul), so there's always a real history entry to step
 * back to. Native has no window.history, so it falls back to `fallback`.
 *
 * `fallback` is also used on web if the history stack is exhausted (e.g. a
 * deep link opened this screen directly with nothing to go back to) —
 * detected via window.history.length, which is 1 on a fresh tab/deep link
 * with no prior in-app navigation.
 */
export const useGoBack = (fallback: Screen, fallbackParam?: string) => {
  const { navigateTo } = useAppContext();

  return () => {
    if (Platform.OS === 'web' && typeof window !== 'undefined' && window.history.length > 1) {
      window.history.back();
      return;
    }
    navigateTo(fallback, fallbackParam);
  };
};
